// Render a minute count for display. Times are null when the recipe's Google
// Doc never stated one, so show "unknown" rather than a made-up number.
export function formatDuration(mins, unit = ' min') {
  return typeof mins === 'number' ? `${mins}${unit}` : 'unknown';
}

/**
 * The time shown on a meal card's "Cooking time" line.
 *
 * This is the recipe's TOTAL time, and the only time on the card. The card
 * used to print the total beside the name and the cook time below it, but
 * about half these recipes state a total without a cook time, so it kept
 * showing a number above the word "unknown" and reading as a contradiction.
 */
export function formatTotalTime(mins) {
  return typeof mins === 'number' ? `${mins} min` : 'unknown';
}

// The key two shopping-list entries share when they name the same thing: the
// text with any trailing qualifier removed, lowercased.
export function shoppingKey(item) {
  const key = item
    .toLowerCase()
    .replace(/\s*\([^)]*\)\s*$/, '') // trailing "(opt)", "(for dough)"
    .replace(/,.*$/, '') // trailing ", cooked", ", chopped"
    .replace(/\s+/g, ' ')
    .trim();
  // An entry that is nothing but a qualifier must not collapse into every
  // other one, so fall back to the whole string.
  return key || item.toLowerCase().trim();
}

/**
 * Drop entries that repeat something already on the list with a different
 * trailing qualifier: "egg noodles, cooked" beside "egg noodles", or
 * "cilantro" beside "cilantro (opt)". This happens when the AI adds a carb
 * the recipe already lists.
 *
 * Only a trailing qualifier is ignored, so different items stay apart:
 * "cherry tomatoes" is not "tomatoes", and "red pepper flakes" is not "red
 * pepper". The shortest spelling wins, since the qualifier is rarely useful
 * when shopping, and the original order is kept.
 */
export function dedupeShoppingList(items) {
  const byKey = new Map();

  for (const item of items) {
    const key = shoppingKey(item);
    const existing = byKey.get(key);
    if (existing === undefined || item.length < existing.length) {
      byKey.set(key, item);
    }
  }

  return [...byKey.values()];
}

// Fisher-Yates shuffle
export function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Select 3 recipes based on user terms.
// fillPool: pool for random fill slots (defaults to recipes). Pass a seen-filtered
// subset so fills avoid repeats without blocking explicit ingredient matches.
// Returns { selectedRecipes, unmatchedTerms }.
export function selectRecipes(recipes, userTerms, fillPool = null) {
  let selectedRecipes;
  let unmatchedTerms = [];

  if (userTerms.length > 0) {
    // Pick at most 1 recipe per search term to ensure meal variety.
    // "chuck roast" → 1 chuck roast recipe + 2 random, not 3 beef meals.
    const pickedIds = new Set();
    const picks = [];

    for (const term of userTerms) {
      if (picks.length >= 3) break;
      const termMatches = shuffle(recipes.filter(r =>
        !pickedIds.has(r.id) &&
        r.ingredients.some(ing => ing.toLowerCase().includes(term.toLowerCase()))
      ));
      if (termMatches.length > 0) {
        picks.push(termMatches[0]);
        pickedIds.add(termMatches[0].id);
      }
    }

    if (picks.length < 3) {
      const fills = fillPool || recipes;
      const isTermMatch = (r) => userTerms.some(term =>
        r.ingredients.some(ing => ing.toLowerCase().includes(term.toLowerCase()))
      );
      // Prefer non-matching recipes for fill slots to enforce variety;
      // fall back to matching ones only if the pool is exhausted.
      const nonMatching = shuffle(fills.filter(r => !pickedIds.has(r.id) && !isTermMatch(r)));
      const matching = shuffle(fills.filter(r => !pickedIds.has(r.id) && isTermMatch(r)));
      const rest = [...nonMatching, ...matching];
      picks.push(...rest.slice(0, 3 - picks.length));
    }

    selectedRecipes = picks;

    const selectedIngredients = selectedRecipes.flatMap(r => r.ingredients.map(i => i.toLowerCase()));
    unmatchedTerms = userTerms.filter(term =>
      !selectedIngredients.some(ing => ing.includes(term.toLowerCase()))
    );
  } else {
    selectedRecipes = shuffle(recipes).slice(0, 3);
  }

  return { selectedRecipes, unmatchedTerms };
}

// Parse "Add to complete meal" value into just ingredient names.
// Returns null if meal is already complete or input can't be parsed cleanly.
export function parseSidesTitle(sidesText) {
  if (!sidesText) return null;
  const trimmed = sidesText.trim();
  if (/^nothing needed/i.test(trimmed)) return null;

  // Strip explanation after em-dash
  let cleaned = trimmed.split(/\s*—\s*/)[0].trim();

  // If the text looks like an AI explanation sentence rather than ingredient names, discard it
  if (/^(this|let'?s|the recipe|it |because)/i.test(cleaned)) return null;

  // Strip parenthetical explanations like "(to incorporate the given ingredient)"
  cleaned = cleaned.replace(/\s*\([^)]*\)/g, '').trim();

  // Strip explanatory clauses after a comma, e.g. "feta, shrimp is not added..."
  cleaned = cleaned.split(/,\s+\w+ (is|are|was|were|will|would|should|has|have|not|to)\b/i)[0].trim();

  // Clean up trailing punctuation
  cleaned = cleaned.replace(/[,+\s]+$/, '').trim();

  return cleaned || null;
}

export const STOCK_ITEMS = [
  // Oils and fats
  'oil', 'olive oil', 'vegetable oil', 'canola oil', 'cooking oil',
  'sesame oil', 'coconut oil',
  'butter', 'margarine', 'cooking spray',
  // Salt
  'salt', 'kosher salt', 'sea salt', 'table salt',
  // Pepper and spices
  'pepper', 'black pepper', 'white pepper', 'ground pepper',
  'paprika', 'cumin', 'chili powder', 'cayenne', 'red pepper flakes',
  'turmeric', 'italian seasoning', 'oregano', 'basil', 'thyme', 'rosemary',
  'bay leaf', 'cinnamon', 'nutmeg', 'cloves', 'ginger powder',
  'garlic powder', 'onion powder', 'dried herbs', 'spices',
  // Aromatics
  'garlic', 'onion', 'onions', 'shallot', 'shallots',
  // Acids
  'vinegar', 'white vinegar', 'red wine vinegar', 'apple cider vinegar',
  'balsamic vinegar', 'rice vinegar',
  'lemon juice', 'lime juice',
  // Condiments
  'soy sauce', 'worcestershire sauce', 'hot sauce', 'fish sauce',
  // Baking basics
  'flour', 'sugar', 'brown sugar', 'white sugar', 'powdered sugar',
  'baking soda', 'baking powder', 'yeast', 'cornstarch',
  // Dairy staples
  'eggs', 'milk', 'water'
];

// Staples that stay staples however they are qualified: "extra-virgin olive
// oil" is still oil. Only words where a qualifier never produces a distinct
// shopping item belong here — not "pepper" (bell pepper is a vegetable),
// not "butter" (peanut butter), not "milk" (coconut milk).
const STOCK_SUFFIX_RE = /\b(?:oil|vinegar|salt)$/;

export function isStockItem(ingredient) {
  const lower = ingredient.toLowerCase().trim();

  const matchesStart = STOCK_ITEMS.some(stock => {
    const escaped = stock.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp('^' + escaped + '(\\s*[,(]|$)', 'i').test(lower);
  });
  if (matchesStart) return true;

  return STOCK_SUFFIX_RE.test(lower.split(/[,(]/)[0].trim());
}

export const SIDE_INGREDIENTS_MAP = {
  'tempeh': 'tempeh',
  'tofu': 'firm tofu',
  'baked tofu': 'firm tofu',
  'chicken breast': 'chicken breast',
  'grilled chicken': 'chicken breast',
  'salmon': 'salmon fillet',
  'rice': 'rice',
  'basmati rice': 'basmati rice',
  'jasmine rice': 'jasmine rice',
  'quinoa': 'quinoa',
  'broccoli': 'broccoli florets',
  'roasted broccoli': 'broccoli florets',
  'steamed broccoli': 'broccoli florets',
  'chickpeas': 'canned chickpeas',
  'black beans': 'canned black beans',
  'crusty bread': 'crusty bread',
  // Salads expand into actual ingredients
  'side salad': ['salad greens', 'tomatoes', 'cucumber'],
  'green salad': ['salad greens', 'tomatoes', 'cucumber'],
  'mixed greens': ['mixed greens'],
  'salad': ['salad greens', 'tomatoes', 'cucumber'],
};

export function extractSideIngredients(sidesText) {
  if (!sidesText || /^nothing needed/i.test(sidesText.trim())) return [];
  const parts = sidesText.split('+').map(s => s.trim()).filter(Boolean);
  const ingredients = [];
  parts.forEach(part => {
    const lowerPart = part.toLowerCase();
    const mapped = Object.entries(SIDE_INGREDIENTS_MAP).find(([key]) => lowerPart.includes(key));
    const items = mapped
      ? (Array.isArray(mapped[1]) ? mapped[1] : [mapped[1]])
      : [part];
    items.forEach(item => {
      if (item && !ingredients.includes(item)) ingredients.push(item);
    });
  });
  return ingredients;
}

export function parseDatesFromMessage(message) {
  const monthMap = {
    jan: 'January', january: 'January',
    feb: 'February', february: 'February',
    mar: 'March', march: 'March',
    apr: 'April', april: 'April',
    may: 'May',
    jun: 'June', june: 'June',
    jul: 'July', july: 'July',
    aug: 'August', august: 'August',
    sep: 'September', september: 'September',
    oct: 'October', october: 'October',
    nov: 'November', november: 'November',
    dec: 'December', december: 'December',
  };

  const dateMatches = message.match(/\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s*(\d{1,2})(?:\s*,?\s*(?:and\s*)?(\d{1,2}))?(?:\s*,?\s*(?:and\s*)?(\d{1,2}))?/i);

  if (!dateMatches) return [];

  const month = monthMap[dateMatches[1].toLowerCase()] || dateMatches[1];
  const dates = [dateMatches[2], dateMatches[3], dateMatches[4]].filter(Boolean);

  return dates.map(day => `${month} ${day}, ${new Date().getFullYear()}`);
}

export function generateICS(meals, dates, recipes) {
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}${month}${day}T${hours}${minutes}00`;
  };

  let ics = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Mama's Menu Maker//EN
CALSCALE:GREGORIAN`;

  meals.forEach((meal, i) => {
    const date = new Date(dates[i] + ' 6:30 PM');
    const endDate = new Date(dates[i] + ' 7:30 PM');
    if (isNaN(date.getTime()) || isNaN(endDate.getTime())) {
      console.error(`Invalid date skipped: ${dates[i]}`);
      return; // skip this meal in forEach
    }

    const recipeObj = recipes ? recipes.find(r => r.name === meal.name) : null;
    const cookTime = meal.totalTime ?? recipeObj?.totalTime ?? null;
    const recipeUrl = meal.url || recipeObj?.url || '';
    const shoppingList = meal.shoppingList || (recipeObj?.ingredients || []);

    const title = meal.addedComponents ? `${meal.name} + ${meal.addedComponents}` : meal.name;
    const ingredientsList = shoppingList.map(ing => `• ${ing}`).join('\\n');
    const description = `Cook time: ${formatDuration(cookTime, ' minutes')}\\n\\nShopping list:\\n${ingredientsList}\\n\\nRecipe: ${recipeUrl}`;

    ics += `
BEGIN:VEVENT
UID:meal-${i}-${Date.now()}@meal-planner
DTSTART:${formatDate(date)}
DTEND:${formatDate(endDate)}
SUMMARY:${title}
DESCRIPTION:${description}
URL:${recipeUrl}
END:VEVENT`;
  });

  ics += '\nEND:VCALENDAR';
  return ics;
}

// ─── Pick history ───────────────────────────────────────────────────────────
//
// Which recipes were chosen on which day, so the planner can steer away from
// what was eaten recently. Keyed by recipe id (the Google Doc id), which
// survives a document being renamed.

export const PICK_HISTORY_KEY = 'mamasMenuMaker.pickHistory';

// Enough to cover the ~4 weeks the planner looks back over, with room to
// spare for weeks that were planned more than once.
export const PICK_HISTORY_MAX = 24;

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

// localStorage throws in private windows and when site data is blocked, and
// is absent entirely under plain Node. Every access goes through these two so
// a storage failure never takes the app down with it.
function readStorage(storage, key) {
  try {
    return storage?.getItem(key) ?? null;
  } catch (e) {
    return null;
  }
}

function writeStorage(storage, key, value) {
  try {
    storage?.setItem(key, value);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Every saved pick, newest first. Returns [] rather than throwing when the
 * stored value is missing, unreadable, or not the shape we wrote.
 */
export function loadPickHistory(storage) {
  const raw = readStorage(storage, PICK_HISTORY_KEY);
  if (!raw) return [];

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  return parsed
    .filter(entry =>
      entry &&
      typeof entry.date === 'string' &&
      Array.isArray(entry.recipes) &&
      !Number.isNaN(Date.parse(entry.date))
    )
    .map(entry => ({
      date: entry.date,
      recipes: entry.recipes
        .filter(r => r && typeof r.id === 'string')
        .map(r => ({ id: r.id, name: typeof r.name === 'string' ? r.name : r.id })),
    }))
    .filter(entry => entry.recipes.length > 0);
}

/**
 * Record one week's picks and return the new history (newest first).
 *
 * Re-saving the same set of recipes replaces the earlier entry instead of
 * adding a second one, so accepting a plan and then downloading its calendar
 * counts as one week rather than two.
 */
export function savePickHistory(recipes, storage, now = new Date()) {
  const picked = (recipes || [])
    .filter(r => r && typeof r.id === 'string')
    .map(r => ({ id: r.id, name: r.name || r.id }));
  if (picked.length === 0) return loadPickHistory(storage);

  const signature = [...picked.map(r => r.id)].sort().join('|');
  const existing = loadPickHistory(storage).filter(entry =>
    [...entry.recipes.map(r => r.id)].sort().join('|') !== signature
  );

  const history = [{ date: now.toISOString(), recipes: picked }, ...existing]
    .slice(0, PICK_HISTORY_MAX);

  writeStorage(storage, PICK_HISTORY_KEY, JSON.stringify(history));
  return history;
}

export function clearPickHistory(storage) {
  writeStorage(storage, PICK_HISTORY_KEY, JSON.stringify([]));
  return [];
}

/** The entries from the last `weeks` weeks, newest first. */
export function recentPicks(history, weeks = 4, now = new Date()) {
  const cutoff = now.getTime() - weeks * WEEK_MS;
  return (history || [])
    .filter(entry => Date.parse(entry.date) >= cutoff)
    .sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
}

/** Recipe ids picked in the last `weeks` weeks, newest first, deduplicated. */
export function recentlyPickedIds(history, weeks = 4, now = new Date()) {
  const ids = recentPicks(history, weeks, now).flatMap(entry => entry.recipes.map(r => r.id));
  return [...new Set(ids)];
}

/**
 * The recent-picks block for the AI prompt, or '' when there is nothing to
 * say. Names rather than ids: the model never sees ids, and a 44-character
 * Google Doc id would only burn output tokens.
 */
export function formatPickHistoryForPrompt(history, weeks = 4, now = new Date()) {
  const recent = recentPicks(history, weeks, now);
  if (recent.length === 0) return '';

  const lines = recent.map(entry => {
    const when = new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `- ${when}: ${entry.recipes.map(r => r.name).join(', ')}`;
  });

  return `Dinners already eaten in the last ${weeks} weeks:\n${lines.join('\n')}\n\nDo not suggest sides that repeat these meals, and vary the proteins away from them.`;
}

// ─── Keep some, redo the rest ───────────────────────────────────────────────

const ORDINAL_WORDS = {
  one: 1, first: 1, '1st': 1,
  two: 2, second: 2, '2nd': 2,
  three: 3, third: 3, '3rd': 3,
};

// Pull the meal numbers out of a fragment like "#2 and the third", ignoring
// the word "meal" and anything out of range.
function mealNumbersIn(fragment, mealCount) {
  if (!fragment) return [];
  const found = [];

  const tokens = fragment.toLowerCase().match(/#?\d+|[a-z]+/g) || [];
  for (const token of tokens) {
    const digits = token.match(/^#?(\d+)$/);
    const n = digits ? parseInt(digits[1], 10) : ORDINAL_WORDS[token];
    if (n >= 1 && n <= mealCount && !found.includes(n)) found.push(n);
  }

  return found;
}

const KEEP_RE = /\b(?:keep|lock|hold|keeping|locked?)\b([^]*?)(?=\b(?:redo|re-?do|re-?roll|rerolling|replace|swap|change|new)\b|$)/i;
const REDO_RE = /\b(?:redo|re-?do|re-?roll|rerolling|replace|swap|change)\b([^]*?)(?=\b(?:keep|lock|hold)\b|$)/i;

// "the others" / "the rest" / "everything else" — a redo with no numbers of
// its own, meaning whatever was not kept.
const REST_RE = /\b(?:the\s+)?(?:others?|rest|remaining|everything\s+else|anything\s+else)\b/i;

/**
 * Read a "keep #2, redo the others" style instruction.
 *
 * Returns { keep, reroll } as 0-based meal indexes, or null when the message
 * is not one of these. The two lists always partition every meal, so the
 * caller can act on either one.
 *
 * Both halves are optional and either can imply the other: "keep 2" rerolls
 * 1 and 3, "redo 1 and 3" keeps 2. A bare "swap 2", with no keep clause and
 * no "the others", is left alone so the existing single-swap path still owns
 * it.
 */
export function parseKeepCommand(message, mealCount = 3) {
  if (!message || mealCount < 1) return null;

  const all = Array.from({ length: mealCount }, (_, i) => i + 1);

  const keepMatch = message.match(KEEP_RE);
  const redoMatch = message.match(REDO_RE);
  if (!keepMatch && !redoMatch) return null;

  const keepNums = keepMatch ? mealNumbersIn(keepMatch[1], mealCount) : [];
  const redoNums = redoMatch ? mealNumbersIn(redoMatch[1], mealCount) : [];

  // "keep 2" and "redo 1, 3" can both be present and must agree; where they
  // disagree the explicit keep wins, because that is the half the user cares
  // about not losing.
  let keep;
  if (keepNums.length > 0) {
    keep = keepNums;
  } else if (redoNums.length > 0) {
    keep = all.filter(n => !redoNums.includes(n));
  } else if (keepMatch && REST_RE.test(keepMatch[1])) {
    // "keep the rest" with nothing named to redo: nothing to act on.
    return null;
  } else if (redoMatch && REST_RE.test(redoMatch[1])) {
    // "redo the others" with no keep clause means redo everything, which is
    // just a fresh roll, not a partial one.
    return null;
  } else {
    return null;
  }

  const reroll = all.filter(n => !keep.includes(n));
  if (reroll.length === 0) return null; // "keep all 3" asks for no work

  return {
    keep: keep.map(n => n - 1).sort((a, b) => a - b),
    reroll: reroll.map(n => n - 1),
  };
}

/**
 * Pick `count` replacement recipes.
 *
 * Avoids everything in `excludeIds` (the meals being kept, plus recently
 * eaten ones) and prefers recipes matching `preferTerms` — the ingredients
 * the user originally asked to use up — but falls back to the wider pool
 * rather than returning short.
 */
export function pickReplacements(recipes, count, excludeIds = [], preferTerms = []) {
  const excluded = new Set(excludeIds);
  const available = recipes.filter(r => !excluded.has(r.id));
  const pool = available.length >= count ? available : recipes.filter(r => !excluded.has(r.id));

  if (preferTerms.length > 0) {
    const matching = shuffle(pool.filter(r =>
      preferTerms.some(term =>
        r.ingredients.some(ing => ing.toLowerCase().includes(term.toLowerCase()))
      )
    ));
    const rest = shuffle(pool.filter(r => !matching.includes(r)));
    return [...matching, ...rest].slice(0, count);
  }

  return shuffle(pool).slice(0, count);
}

// ─── Shopping checklist ─────────────────────────────────────────────────────

/**
 * Every ingredient the week's meals need, each listed once, grouped under the
 * first meal that calls for it.
 *
 * Takes the same meal objects the chat already holds ({ recipe, sides }).
 * Pantry staples are filtered out and near-duplicate spellings collapse, the
 * same way the per-meal shopping lists on the cards do, so the checklist and
 * the cards never disagree about what has to be bought.
 *
 * Each item records every meal that uses it, so an ingredient shared by two
 * meals is bought once but still traceable to both.
 */
export function buildShoppingChecklist(meals) {
  const byKey = new Map();

  (meals || []).forEach((meal, index) => {
    const mealNum = index + 1;
    const raw = [
      ...(meal?.recipe?.ingredients || []),
      ...extractSideIngredients(meal?.sides),
    ];

    for (const item of dedupeShoppingList(raw).filter(i => !isStockItem(i))) {
      const key = shoppingKey(item);
      const existing = byKey.get(key);

      if (!existing) {
        byKey.set(key, { key, label: item, firstMeal: mealNum, meals: [mealNum] });
        continue;
      }
      // Keep the shortest spelling, matching dedupeShoppingList: the trailing
      // qualifier ("cooked", "chopped") is noise when shopping.
      if (item.length < existing.label.length) existing.label = item;
      if (!existing.meals.includes(mealNum)) existing.meals.push(mealNum);
    }
  });

  const items = [...byKey.values()];

  const groups = (meals || []).map((meal, index) => ({
    mealNum: index + 1,
    mealName: meal?.recipe?.name?.trim() || `Meal ${index + 1}`,
    items: items.filter(item => item.firstMeal === index + 1),
  })).filter(group => group.items.length > 0);

  return { groups, items };
}

/**
 * The checked items as plain lines, ready to paste into a list app.
 * `checkedKeys` holds the keys of items the user ticked.
 */
export function checklistToText(checklist, checkedKeys) {
  const checked = new Set(checkedKeys || []);
  return (checklist?.items || [])
    .filter(item => checked.has(item.key))
    .map(item => item.label)
    .join('\n');
}

// ─── Calendar download ──────────────────────────────────────────────────────

// What someone types to get the ICS file once a calendar is waiting. The app
// tells them to say "download calendar", so that phrase must work; the rest
// are the obvious ways of saying the same thing.
const CALENDAR_DOWNLOAD_RE = /\b(?:download|create|make|save|send|get|generate|give)\b[^]*\b(?:calendar|ics|file|it)\b|\b(?:download|ics)\b|^\s*(?:yes|yep|yeah|ok|okay|sure|do it|please)\s*[.!]?\s*$/i;

/**
 * Whether this message is asking for the pending calendar file.
 *
 * Only consulted while a calendar is actually waiting, which is what makes a
 * bare "yes" safe to read as "yes, download it".
 */
export function isCalendarDownloadRequest(message) {
  if (!message) return false;
  return CALENDAR_DOWNLOAD_RE.test(message.trim());
}

// ─── Ingredient terms ───────────────────────────────────────────────────────

// Words left over from a phrase like "something quick" or "any easy dinners".
// They name no ingredient, so searching for them would only narrow the pool
// to nothing and waste a slot.
export const FILLER_TERMS = [
  'something', 'anything', 'some', 'any', 'a', 'an', 'the',
  'meal', 'meals', 'dinner', 'dinners', 'recipe', 'recipes', 'food',
  'please', 'thanks', 'plan', 'ideas', 'idea', 'easy', 'simple', 'nice',
  'tonight', 'tomorrow', 'week', 'this', 'that', 'make', 'want', 'need',
];

const isFiller = (word) => FILLER_TERMS.includes(word.toLowerCase());

// Shave filler off both ends of a phrase, leaving the ingredient in the
// middle: "some chicken" is a search for chicken, "some easy dinners" is a
// search for nothing. Only the edges are touched, so a real ingredient whose
// name contains one of these words ("simple syrup") survives intact.
function trimFiller(phrase) {
  const words = phrase.split(/\s+/).filter(Boolean);
  let start = 0;
  let end = words.length;
  while (start < end && isFiller(words[start])) start++;
  while (end > start && isFiller(words[end - 1])) end--;
  return words.slice(start, end).join(' ');
}

/**
 * Split a message into the ingredient terms worth searching for.
 *
 * Filler words are dropped, so "something quick" (once the time phrase is
 * lifted out) searches for nothing rather than for an ingredient called
 * "something".
 */
export function parseIngredientTerms(message) {
  if (!message) return [];
  return message
    .split(/[,/]|and\b/i)
    .map(t => t.replace(/^[\s\-–—:]+|[\s\-–—:]+$/g, '').trim())
    .map(trimFiller)
    .filter(t => t.length > 1);
}

// ─── Time limits ────────────────────────────────────────────────────────────

const TIME_LIMIT_RE = /\b(?:under|below|less\s+than|within|no\s+more\s+than|at\s+most|max(?:imum)?(?:\s+of)?|in)\s+(\d{1,3})\s*(m|min|mins|minute|minutes|h|hr|hrs|hour|hours)?\b|\b(\d{1,3})\s*(m|min|mins|minute|minutes|h|hr|hrs|hour|hours)\s+(?:or\s+less|or\s+under|max(?:imum)?)\b/i;

// "quick" and friends, with no number attached.
const QUICK_RE = /\b(quick|quickly|fast|speedy|weeknight|short)\b/i;
export const QUICK_LIMIT_MINUTES = 30;

/**
 * The cooking-time ceiling the user asked for, in minutes, or null.
 *
 * `cleaned` is the message with the time phrase removed, so the rest can
 * still be read as a list of ingredients: "quick, chicken" means chicken
 * recipes under 30 minutes, not an ingredient called "quick".
 */
export function parseTimeLimit(message) {
  if (!message) return null;

  const match = message.match(TIME_LIMIT_RE);
  if (match) {
    const amount = parseInt(match[1] ?? match[3], 10);
    const unit = (match[2] ?? match[4] ?? 'min').toLowerCase();
    const minutes = /^h/.test(unit) ? amount * 60 : amount;
    if (minutes > 0) {
      return { maxMinutes: minutes, cleaned: message.replace(match[0], ' ').replace(/\s+/g, ' ').trim() };
    }
  }

  const quick = message.match(QUICK_RE);
  if (quick) {
    return {
      maxMinutes: QUICK_LIMIT_MINUTES,
      cleaned: message.replace(quick[0], ' ').replace(/\s+/g, ' ').trim(),
    };
  }

  return null;
}

/**
 * Recipes that fit inside `maxMinutes`.
 *
 * A recipe whose document never stated a time has totalTime null, and null is
 * not a short time — it is no information. Promising a 30-minute dinner on a
 * recipe that might take two hours is worse than leaving it out, so unknown
 * times are excluded rather than treated as zero.
 *
 * Returns null when nothing fits, so the caller can say so instead of
 * silently serving an unfiltered list.
 */
export function filterByTimeLimit(recipes, maxMinutes) {
  if (typeof maxMinutes !== 'number') return recipes;
  const fits = (recipes || []).filter(r => typeof r.totalTime === 'number' && r.totalTime <= maxMinutes);
  return fits.length > 0 ? fits : null;
}

// ─── Choosing between recipes ───────────────────────────────────────────────

/**
 * The handful of ingredients that say what a dish actually is.
 *
 * Pantry staples are dropped first, since "salt, olive oil, garlic" describes
 * every recipe equally and distinguishes none of them. Trailing preparation
 * notes go too: when choosing, "beef chuck" is the useful part of "beef chuck
 * or rib steak - sliced thin against the grain".
 */
export function summarizeIngredients(ingredients, max = 3) {
  const shoppable = dedupeShoppingList(ingredients || []).filter(item => !isStockItem(item));

  return shoppable.slice(0, max).map(item =>
    item
      .replace(/\s*\([^)]*\)/g, '')     // "(opt)", "(for dough)"
      .replace(/\s*[,-].*$/, '')          // ", chopped", " - sliced thin"
      .replace(/\s+or\s+.*$/i, '')       // "beef chuck or rib steak"
      .replace(/^(?:a\s+)?(?:bag|box|can|jar|package|pkg|bunch|head|pound|lb)\s+of\s+/i, '')
      .replace(/^(?:good|fresh|large|small|medium)\s+/i, '')
      .replace(/^juice\s+of\s+\d*\s*/i, '')
      .replace(/\s+/g, ' ')
      .trim()
  ).filter(Boolean);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * How long ago this recipe was last eaten, phrased for a glance.
 *
 * Returns null when it has never been picked, which the caller shows as "new
 * to you": the absence of history is itself a reason to choose something.
 */
export function lastEatenLabel(recipeId, history, now = new Date()) {
  const entries = (history || [])
    .filter(entry => entry.recipes.some(r => r.id === recipeId))
    .map(entry => Date.parse(entry.date))
    .filter(t => !Number.isNaN(t))
    .sort((a, b) => b - a);

  if (entries.length === 0) return null;

  const days = Math.floor((now.getTime() - entries[0]) / DAY_MS);
  if (days <= 0) return 'made today';
  if (days === 1) return 'made yesterday';
  if (days < 7) return `made ${days} days ago`;

  const weeks = Math.floor(days / 7);
  if (weeks === 1) return 'made last week';
  if (weeks < 9) return `made ${weeks} weeks ago`;

  const months = Math.floor(days / 30);
  return months <= 1 ? 'made last month' : `made ${months} months ago`;
}
